/**
 * Access requests, and the two approvals that gate them.
 *
 * The screen's job is to make the scope **legible before it is approved**. An approver who cannot
 * see what they are agreeing to will agree to whatever is put in front of them, and dual approval
 * then measures nothing. So the capabilities are spelled out in words rather than shown as a set
 * of flags, and the ones that let data leave the client's estate are called out.
 *
 * Both approve buttons are always rendered, and disabled with a stated reason when the current
 * user cannot use them. A missing button reads as a broken page; a disabled one that says "the
 * client's approval is not yours to give" teaches the model in one glance.
 */

import { useCallback, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

const STATE_TONE: Record<string, Tone> = {
  pending_provider: 'info',
  pending_client: 'warn',
  approved: 'ok',
  refused: 'muted',
  expired: 'muted',
  withdrawn: 'muted',
};

/** The capabilities worth naming, and the ones worth naming loudly. */
const CAPABILITY_LABELS: { key: string; label: string; exfiltration?: boolean }[] = [
  { key: 'view', label: 'observe' },
  { key: 'input_control', label: 'type' },
  { key: 'clipboard_to_client', label: 'paste in' },
  { key: 'clipboard_to_engineer', label: 'copy out', exfiltration: true },
  { key: 'file_push', label: 'send files' },
  { key: 'file_pull', label: 'take files', exfiltration: true },
  { key: 'elevation', label: 'elevate' },
  { key: 'apply_changes', label: 'change things' },
  { key: 'auto_apply', label: 'change without asking' },
  { key: 'discover', label: 'sweep' },
];

interface DerivedScope {
  targets?: string[];
  dropped_targets?: string[];
  capabilities?: Record<string, boolean>;
  recording?: string;
  ci_name?: string;
}

export function AccessRequests(): JSX.Element {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const { data, error, loading } = useAsync(() => api.accessRequests(100), [revision]);
  const me = useAsync(() => api.whoami(), []);
  const [writeError, setWriteError] = useState<unknown>(null);

  const rows = data ?? [];
  const roles = me.data?.roles ?? [];
  const canProvider = roles.some((r) => ['approver', 'tenant_admin', 'platform_admin'].includes(r));
  // Only the client approver, matching the server. Rendering the button as available to anyone
  // else would teach the wrong model even though the server refuses it.
  const canClient = roles.includes('client_approver');

  const awaiting = rows.filter(
    (r) => r.state === 'pending_provider' || r.state === 'pending_client',
  );
  const approved = rows.filter((r) => r.state === 'approved').length;

  const act = (fn: Promise<unknown>): void => {
    setWriteError(null);
    void fn.then(refresh).catch((e: unknown) => {
      setWriteError(e);
    });
  };

  const decline = (id: string): void => {
    const reason = window.prompt('Why is this being refused? The requester will see it.');
    if (reason === null || reason.trim() === '') return;
    act(api.refuseAccess(id, reason));
  };

  return (
    <>
      <PageHeader
        title="Access requests"
        subtitle="Scoped, time-boxed, and granted by two parties — the provider that the work is warranted, the client that somebody may enter their estate."
      />

      <ErrorBox error={error} />
      <ErrorBox error={writeError} />

      <Card>
        <StatRow>
          <Stat
            label="Awaiting a decision"
            value={awaiting.length}
            tone={awaiting.length > 0 ? 'warn' : 'neutral'}
          />
          <Stat
            label="Waiting on the client"
            value={rows.filter((r) => r.state === 'pending_client').length}
            hint="the provider has agreed"
          />
          <Stat label="Approved" value={approved} tone={approved > 0 ? 'ok' : 'neutral'} />
          <Stat label="Total shown" value={rows.length} />
        </StatRow>
      </Card>

      <Card
        title="Requests"
        subtitle="The scope is derived, never typed: the playbook's request intersected with the configuration item's ceiling. Neither approver can widen it."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Ticket', 'State', 'Scope', 'Approvals', 'Expires', '']}
            empty="No access requests. Raise one from a ticket that has been triaged."
          >
            {rows.map((r) => {
              const scope = r.derived_scope as DerivedScope;
              const caps = scope.capabilities ?? {};
              const granted = CAPABILITY_LABELS.filter((c) => caps[c.key] === true);

              return (
                <tr key={r.id}>
                  <td>
                    <div className="table__strong">{r.reference}</div>
                    {/* Rendered as text. A ticket title is attacker-controlled. */}
                    <Formula>{r.title}</Formula>
                  </td>
                  <td>
                    <Badge tone={STATE_TONE[r.state] ?? 'muted'}>
                      {r.state.replace(/_/g, ' ')}
                    </Badge>
                    {r.decision_reason !== null && <Formula>{r.decision_reason}</Formula>}
                  </td>
                  <td>
                    <Formula>{scope.ci_name ?? '—'}</Formula>
                    <div>
                      {granted.length === 0 ? (
                        <Badge tone="muted">nothing</Badge>
                      ) : (
                        granted.map((c) => (
                          <Badge key={c.key} tone={c.exfiltration === true ? 'danger' : 'muted'}>
                            {c.label}
                          </Badge>
                        ))
                      )}
                    </div>
                    <Formula>{(scope.targets ?? []).join(', ')}</Formula>
                    {(scope.dropped_targets ?? []).length > 0 && (
                      /* Dropped by the ceiling. Shown because a consistently dropped target
                         usually means the CMDB is wrong rather than the playbook. */
                      <Formula>dropped: {(scope.dropped_targets ?? []).join(', ')}</Formula>
                    )}
                  </td>
                  <td>
                    <Formula>
                      provider {r.provider_approved_by === null ? '—' : '✓'} · client{' '}
                      {r.client_approved_by === null ? '—' : '✓'}
                    </Formula>
                    {r.permits_session && <Badge tone="ok">session may open</Badge>}
                  </td>
                  <td>
                    <Formula>{r.expires_at.replace('T', ' ').slice(0, 16)}</Formula>
                  </td>
                  <td>
                    {r.state === 'pending_provider' && (
                      <button
                        type="button"
                        className="button button--small"
                        disabled={!canProvider}
                        title={canProvider ? '' : 'you do not hold provider approval'}
                        onClick={() => {
                          act(api.approveAccess(r.id, 'provider'));
                        }}
                      >
                        approve (provider)
                      </button>
                    )}
                    {r.state === 'pending_client' && (
                      <button
                        type="button"
                        className="button button--small"
                        disabled={!canClient}
                        title={
                          canClient
                            ? ''
                            : "the client's approval is not yours to give — it is a separate authority"
                        }
                        onClick={() => {
                          act(api.approveAccess(r.id, 'client'));
                        }}
                      >
                        approve (client)
                      </button>
                    )}
                    {(r.state === 'pending_provider' || r.state === 'pending_client') && (
                      <button
                        type="button"
                        className="button button--small"
                        disabled={!canProvider && !canClient}
                        onClick={() => {
                          decline(r.id);
                        }}
                      >
                        refuse
                      </button>
                    )}
                    {/*
                      Offered on `permits_session` — the server's own answer, not this screen's
                      reading of the approval columns. It is also not a claim that the session will
                      open: a contract may be unsigned, revoked, or expired, and the caller may not
                      be one of the engineers the client named. Every one of those is a refusal with
                      a sentence explaining it, which is a better place to learn than a button that
                      is missing for a reason nobody can see.
                    */}
                    {r.permits_session && (
                      <Link className="button button--small" to={`/access-requests/${r.id}/session`}>
                        open session
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>
    </>
  );
}
